import { actionTypes, buildingData, researchData, shipData, defenseData } from '../data';
import { createPlotRecorder } from './snapshot';
import { NextActionReady, StateFastForward, CompleteAction } from './core';
import { SmartActionChooser } from './schedule';
import { makeInvalidEntry, makeTimelineEntry } from './timelineEntries';
import { handleUpgradeBuilding } from './handlers/upgradeBuilding';
import { handleResearch } from './handlers/research';
import { handleBuildShip } from './handlers/buildShip';
import { handleBuildDefense } from './handlers/buildDefense';
import { handleSetProduction } from './handlers/setProduction';
import { handleTransport } from './handlers/transport';

const HANDLERS = {
  [actionTypes.UPGRADE_BUILDING]: handleUpgradeBuilding,
  [actionTypes.RESEARCH]: handleResearch,
  [actionTypes.BUILD_SHIP]: handleBuildShip,
  [actionTypes.BUILD_DEFENSE]: handleBuildDefense,
  [actionTypes.SET_PROD]: handleSetProduction,
  [actionTypes.TRANSPORT]: handleTransport,
};

// A deactivated action is echoed into the timeline as a zero-length marker
// without ever being simulated, so it has no assigned duration yet — this
// mirrors the exact text the legacy inline ternary produced.
const describeDeactivatedAction = (state, action) => {
  const { type, payload } = action;
  switch (type) {
    case actionTypes.UPGRADE_BUILDING:
      return `Start ${buildingData[payload.building].name} ${state.planets[payload.planetId].buildings[payload.building] + 1}`;
    case actionTypes.RESEARCH:
      return `Start ${researchData[payload.tech].name} ${state.research[payload.tech] + 1}`;
    case actionTypes.BUILD_SHIP:
      return `Start ${shipData[payload.ship].name} ${payload.count}`;
    case actionTypes.BUILD_DEFENSE:
      return `Start ${defenseData[payload.defense].name} ${payload.count}`;
    case actionTypes.SET_PROD:
      return 'Set Production';
    case actionTypes.TRANSPORT:
      return 'Start Transport';
    default:
      return 'Unknown action';
  }
};

export const GameReducer = (initialState, origBuildOrder) => {
  // Deep copy so the caller's state/buildOrder are never mutated.
  let state = JSON.parse(JSON.stringify(initialState));
  const buildOrder = JSON.parse(JSON.stringify(origBuildOrder));

  const timeline = [];
  const recorder = createPlotRecorder();

  Object.keys(state.planets).forEach((planetId) => {
    recorder.record(state, planetId, 'Initial state');
  });

  let index;
  let action;
  // eslint-disable-next-line no-cond-assign
  while (([index, action] = SmartActionChooser(state, buildOrder)) && index !== undefined && action !== undefined) {
    const { type, payload, deactivated } = action;

    if (deactivated === true) {
      timeline.push({
        valid: true,
        editable: true,
        deactivated: true,
        index,
        planetId: payload.planetId,
        startTime: state.planets[payload.planetId].time,
        endTime: state.planets[payload.planetId].time,
        summary: describeDeactivatedAction(state, action),
      });
      continue;
    }

    if (state.planets[payload.planetId] === undefined) {
      timeline.push(makeInvalidEntry(state, payload.planetId, index, 'Unknown planet'));
      continue;
    }

    const handler = HANDLERS[type];
    if (handler) {
      state = handler(state, timeline, recorder, action, index);
    }
  }

  // At last, wait for every planet's remaining in-progress actions to finish.
  for (const planetId of Object.keys(state.planets)) {
    let nextAction;
    // eslint-disable-next-line no-cond-assign
    while ((nextAction = NextActionReady(state, planetId)) !== undefined) {
      timeline.push(makeTimelineEntry('wait', state, nextAction));
      // Uses nextAction.payload.planetId (not the outer loop's planetId),
      // matching legacy exactly — for an incoming transport this is the
      // *source* planet, found while iterating the *target*'s queue.
      state = StateFastForward(state, nextAction.payload.planetId, nextAction.duration, recorder);
      state = CompleteAction(state, nextAction, recorder);
    }
  }

  // Make sure all planets end at the same global time.
  for (const planetId of Object.keys(state.planets)) {
    if (state.planets[planetId].time < state.time) {
      const duration = state.time - state.planets[planetId].time;
      timeline.push({
        valid: true,
        editable: false,
        forceShow: true,
        index: 'IDLE.' + planetId,
        planetId,
        startTime: state.planets[planetId].time,
        endTime: state.planets[planetId].time + duration,
        summary: 'Idle',
      });
      state = StateFastForward(state, planetId, duration, recorder);
    }
  }

  Object.keys(state.planets).forEach((planetId) => {
    recorder.record(state, planetId, 'Final state');
  });

  return [state, timeline, recorder.data()];
};
